import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';
import { Branch } from './branchApi';

export interface PhysicalMachine {
  id: number;
  machineCode: string;
  machineName: string;
  description?: string;
  erpCode?: string;
  machineModel?: string;
  machineEff?: string;
  categoryId?: number;
  categoryName?: string; // Keep for backward compatibility/display
  usedFor?: string;
  smp?: string;
  branch?: Branch;
  branchId?: number;
  departmentId?: number;
  department?: string; // Keep for backward compatibility/display
  workAreaId?: number;
  workArea?: string; // Keep for backward compatibility/display
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PhysicalMachineCreateRequest {
  machineCode?: string;
  machineName: string;
  erpCode?: string;
  machineModel?: string;
  machineEff?: string;
  categoryId?: number;
  usedFor?: string;
  smp?: string;
  branchId?: number;
  departmentId?: number;
  workAreaId?: number;
  isActive?: boolean;
}

export interface PhysicalMachineUpdateRequest extends PhysicalMachineCreateRequest { id: number; }

class PhysicalMachineApiService extends BaseApiService {
  async getMachines(branchId?: number): Promise<PhysicalMachine[]> {
    try {
      const response = await this.makeRequest<ApiResponse<PhysicalMachine[]>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.LIST(branchId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch physical machines');
    } catch (error) {
      // console.error('Error fetching physical machines:', error);
      toast.error('Failed to fetch physical machines');
      throw error;
    }
  }

  async getMachineById(id: string): Promise<PhysicalMachine> {
    try {
      const response = await this.makeRequest<ApiResponse<PhysicalMachine>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch physical machine');
    } catch (error) {
      // console.error('Error fetching physical machine:', error);
      toast.error('Failed to fetch physical machine details');
      throw error;
    }
  }

  async createMachine(data: PhysicalMachineCreateRequest): Promise<PhysicalMachine> {
    try {
      const response = await this.makeRequest<ApiResponse<PhysicalMachine>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.CREATE,
        { method: HTTP_METHODS.POST, body: JSON.stringify(data) }
      );
      if (response.success) {
        toast.success('Physical machine created successfully');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create physical machine');
    } catch (error) {
      // console.error('Error creating physical machine:', error);
      toast.error('Failed to create physical machine');
      throw error;
    }
  }

  async updateMachine(id: string, data: PhysicalMachineUpdateRequest): Promise<PhysicalMachine> {
    try {
      const response = await this.makeRequest<ApiResponse<PhysicalMachine>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.UPDATE(id),
        { method: HTTP_METHODS.PUT, body: JSON.stringify(data) }
      );
      if (response.success) {
        toast.success('Physical machine updated successfully');
        return response.data;
      }
      throw new Error(response.message || 'Failed to update physical machine');
    } catch (error) {
      // console.error('Error updating physical machine:', error);
      toast.error('Failed to update physical machine');
      throw error;
    }
  }

  async deleteMachine(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      if (response.success) {
        toast.success('Physical machine deleted successfully');
        return;
      }
      throw new Error(response.message || 'Failed to delete physical machine');
    } catch (error) {
      // console.error('Error deleting physical machine:', error);
      toast.error('Failed to delete physical machine');
      throw error;
    }
  }

  async generateMachineCode(): Promise<string> {
    try {
      const response = await this.makeRequest<ApiResponse<string>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.GENERATE_CODE,
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data) return response.data;
      throw new Error(response.message || 'Failed to generate physical machine code');
    } catch (error) {
      const fallback = `MCH-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;
      return fallback;
    }
  }

  async getMachinesByWorkAreaId(workAreaId: number, branchId?: number): Promise<PhysicalMachine[]> {
    try {
      const response = await this.makeRequest<ApiResponse<PhysicalMachine[]>>(
        API_ENDPOINTS.PHYSICAL_MACHINE.BY_WORK_AREA_ID(workAreaId.toString(), branchId),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch machines by work area');
    } catch (error) {
      // console.error('Error fetching machines by work area:', error);
      toast.error('Failed to fetch machines');
      throw error;
    }
  }
}

export const physicalMachineApiService = new PhysicalMachineApiService();

